import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	createProcessObjectSchema,
	CreateProcessObjectFormSchema,
	LINE_VOLTAGE_OPTIONS
} from './create-process-object-schema';
import { CreateProcessObjectRequest, ProcessObjectListLoaderResponse } from '../list/types';
import { useLoaderData } from 'react-router';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete';
import { useMutation } from '@tanstack/react-query';
import { getTtmsLabel, createProcessObject } from '../list/constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import Spinner from '@/components/spinner/Spinner';

export type CreateProcessObjectFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
};
export const CreateProcessObjectForm = (props: CreateProcessObjectFormProps) => {
	const { isOpen, handleToggleForm } = props;

	const {
		ttmsList: { data: ttmsListItems }
	} = useLoaderData() as ProcessObjectListLoaderResponse;

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const createProcessObjectForm = useForm<CreateProcessObjectFormSchema>({
		resolver: zodResolver(createProcessObjectSchema),
		defaultValues: {
			name: '',
			voltageLines: [],
			primaryTtms: [],
			secondaryTtms: []
		}
	});
	const { isDesktop } = useIsDesktopSize();
	const { isPending: isSaving, mutate: mutateCreateProcessObject } = useMutation({
		mutationFn: createProcessObject,
		mutationKey: ['process-objects'],
		onSuccess: () => {
			createProcessObjectForm.reset();
			handleToggleForm();
			setShowConfirmation(false);
			toast('Process object saved successfully');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleSaveProcessObject = () => {
		const submitForm = createProcessObjectForm.handleSubmit(values => {
			const payload: CreateProcessObjectRequest = {
				name: values.name,
				voltageLines: values.voltageLines.map(v => parseInt(v.value)),
				primaryTtms: parseInt(values.primaryTtms[0].value),
				secondaryTtms: parseInt(values.secondaryTtms[0].value)
			};
			mutateCreateProcessObject(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button asChild variant="outline" onClick={handleToggleForm} className="flex justify-start w-fit">
					<span>
						<Plus />
						New process object
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-5 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>Create a new process object</SheetTitle>
					<SheetTitle className="text-[10px] font-normal">{`(Also creates a site and one or more locations)`}</SheetTitle>
					<SheetDescription>Fill in the details. Click save when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...createProcessObjectForm}>
					<form className="p-4 flex flex-col gap-3">
						<FormField
							name="name"
							control={createProcessObjectForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Process object name</FormLabel>
									<FormControl>
										<Input
											type="text"
											placeholder="Process object name"
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<RHFAutoComplete
							control={createProcessObjectForm.control}
							name="voltageLines"
							label="Line voltage"
							placeholder="Select line voltage"
							items={LINE_VOLTAGE_OPTIONS}
							isMultiSelect
							itemToOption={item => ({
								value: item.toString(),
								label: item.toString()
							})}
						/>

						<RHFAutoComplete
							control={createProcessObjectForm.control}
							name="primaryTtms"
							label="Primary TTMS"
							placeholder="Select primary TTMS"
							items={ttmsListItems}
							itemToOption={item => ({
								value: item.id.toString(),
								label: getTtmsLabel(item)
							})}
						/>

						<RHFAutoComplete
							control={createProcessObjectForm.control}
							name="secondaryTtms"
							label="Secondary TTMS"
							placeholder="Select secondary TTMS"
							items={ttmsListItems}
							itemToOption={item => ({
								value: item.id.toString(),
								label: getTtmsLabel(item)
							})}
						/>
					</form>
				</Form>
				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}
				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to save this process object?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isSaving}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleSaveProcessObject}
									disabled={isSaving}
								>
									{error ? 'Try again ' : 'Save'}
									{isSaving && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};
